import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Trash2, ShieldCheck, AlertCircle, Building2, User } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Card listing every required credential for one person: the baseline catalog
 * + every agency requirement that applies (because the person has visits at
 * that agency) + any one-off personnel requirements an admin has added.
 *
 * Provides inline Add-One-Off and Remove-One-Off actions. Baseline and
 * agency rules are read-only from here (they're managed at the catalog /
 * agency profile level respectively).
 */
export function PersonnelRequiredCredentials({ personnelId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    if (!personnelId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/personnel/${personnelId}/required-credentials`);
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load required credentials');
    } finally {
      setLoading(false);
    }
  }, [personnelId]);

  useEffect(() => { load(); }, [load]);

  const removeOneOff = async (reqId) => {
    if (!window.confirm('Remove this required credential for this person?')) return;
    try {
      await axios.delete(`${API}/personnel/${personnelId}/required-credentials/${reqId}`);
      toast.success('Requirement removed');
      load();
    } catch (e) {
      toast.error('Failed to remove requirement');
    }
  };

  if (loading && !data) {
    return <div className="text-sm text-muted-foreground">Loading required credentials…</div>;
  }
  if (!data) return null;

  const sourceLabel = (sources) => {
    if (sources.some(s => s.source === 'personnel')) return 'one-off';
    if (sources.some(s => s.source === 'agency')) return 'agency';
    return 'baseline';
  };

  const sourceBadge = (sources) => {
    const label = sourceLabel(sources);
    if (label === 'baseline') {
      return <Badge variant="outline" className="text-[10px]">Baseline</Badge>;
    }
    if (label === 'agency') {
      const agencies = sources.filter(s => s.source === 'agency').map(s => s.agency).filter(Boolean);
      return (
        <Badge className="text-[10px] bg-blue-100 text-blue-700 border-blue-200" title={`Required by: ${agencies.join(', ')}`}>
          <Building2 size={10} className="mr-0.5" />
          Agency ({agencies.length})
        </Badge>
      );
    }
    return (
      <Badge className="text-[10px] bg-purple-100 text-purple-700 border-purple-200">
        <User size={10} className="mr-0.5" />
        One-off
      </Badge>
    );
  };

  return (
    <div className="space-y-3" data-testid="personnel-required-credentials">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="font-medium text-sm flex items-center gap-2">
            <ShieldCheck size={14} className="text-emerald-600" />
            Required Credentials ({data.required.length})
          </h4>
          <p className="text-xs text-muted-foreground">
            Baseline + agencies they work at ({data.agencies.length}) + one-offs ({data.personnel_one_offs.length})
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setShowAdd(true)}
          data-testid="add-personnel-required-btn"
        >
          <Plus size={14} className="mr-1" /> Add One-Off
        </Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs">
            <tr>
              <th className="text-left px-3 py-2 font-medium">Credential</th>
              <th className="text-left px-3 py-2 font-medium">Source</th>
              <th className="text-left px-3 py-2 font-medium">Expiration</th>
              <th className="text-left px-3 py-2 font-medium">Blocks scheduling</th>
              <th className="w-12"></th>
            </tr>
          </thead>
          <tbody>
            {data.required.map((r) => {
              const oneOff = data.personnel_one_offs.find(o => o.document_type === r.type);
              return (
                <tr key={r.type} className="border-t">
                  <td className="px-3 py-2">{r.type}</td>
                  <td className="px-3 py-2">{sourceBadge(r.sources)}</td>
                  <td className="px-3 py-2 text-muted-foreground">
                    {oneOff && oneOff.expiration_mode === 'fixed' && (
                      <span title={`Required by ${oneOff.fixed_expiration_date}`}>
                        By {oneOff.fixed_expiration_date || '—'}
                      </span>
                    )}
                    {oneOff && oneOff.expiration_mode === 'rolling' && (
                      <span>Every {oneOff.rolling_period_months || 'N/A'} mo</span>
                    )}
                    {!oneOff && '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.blocks_scheduling ? (
                      <Badge className="text-[10px] bg-red-100 text-red-700">Yes</Badge>
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {oneOff && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeOneOff(oneOff.id)}
                        className="text-destructive hover:text-destructive"
                        title="Remove one-off requirement"
                        data-testid={`remove-required-${oneOff.id}`}
                      >
                        <Trash2 size={13} />
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {data.required.length === 0 && (
              <tr>
                <td colSpan={5} className="text-center py-4 text-sm text-muted-foreground">
                  No required credentials configured.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showAdd && (
        <AddOneOffModal
          personnelId={personnelId}
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); load(); }}
        />
      )}
    </div>
  );
}


function AddOneOffModal({ personnelId, onClose, onSaved }) {
  const [docType, setDocType] = useState('');
  const [mode, setMode] = useState('rolling');
  const [fixedDate, setFixedDate] = useState('');
  const [rollingMonths, setRollingMonths] = useState(12);
  const [blocks, setBlocks] = useState(false);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!docType.trim()) {
      toast.error('Credential name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        document_type: docType.trim(),
        expiration_mode: mode,
        blocks_scheduling: blocks,
        notes: notes.trim() || null,
      };
      if (mode === 'fixed') payload.fixed_expiration_date = fixedDate || null;
      if (mode === 'rolling') payload.rolling_period_months = Number(rollingMonths) || null;
      await axios.post(`${API}/personnel/${personnelId}/required-credentials`, payload);
      toast.success('Requirement added');
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to add requirement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg shadow-xl w-full max-w-md p-5 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <h3 className="font-medium text-lg">Add One-Off Required Credential</h3>
          <p className="text-sm text-muted-foreground">In addition to the baseline + agency requirements.</p>
        </div>

        <div className="space-y-3">
          <div>
            <label className="text-xs font-medium block mb-1">Credential name</label>
            <Input
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              placeholder="e.g. Background Check, Bloodborne Pathogens"
              data-testid="oneoff-doctype-input"
            />
          </div>

          <div>
            <label className="text-xs font-medium block mb-1">Renewal cadence</label>
            <div className="flex gap-2">
              <Button
                type="button"
                size="sm"
                variant={mode === 'rolling' ? 'default' : 'outline'}
                onClick={() => setMode('rolling')}
              >
                Rolling
              </Button>
              <Button
                type="button"
                size="sm"
                variant={mode === 'fixed' ? 'default' : 'outline'}
                onClick={() => setMode('fixed')}
              >
                Fixed date
              </Button>
            </div>
            {mode === 'rolling' && (
              <div className="mt-2">
                <label className="text-xs text-muted-foreground">Renew every</label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={1}
                    max={120}
                    value={rollingMonths}
                    onChange={(e) => setRollingMonths(e.target.value)}
                    className="w-24"
                  />
                  <span className="text-sm">months</span>
                </div>
              </div>
            )}
            {mode === 'fixed' && (
              <div className="mt-2">
                <label className="text-xs text-muted-foreground">Required by date</label>
                <Input
                  type="date"
                  value={fixedDate}
                  onChange={(e) => setFixedDate(e.target.value)}
                />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={blocks}
              onChange={(e) => setBlocks(e.target.checked)}
            />
            <span className="flex items-center gap-1">
              <AlertCircle size={13} className="text-amber-500" />
              Block scheduling when missing (off by default)
            </span>
          </label>

          <div>
            <label className="text-xs font-medium block mb-1">Notes (optional)</label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Why this person needs it…"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="oneoff-save-btn">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PersonnelRequiredCredentials;
